import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ToastService } from 'src/app/services/toast.service';
import { VendorService } from 'src/app/services/marketplace/vendor.service';
import { ConnectAccountStatus, Vendor } from 'src/app/services/marketplace/marketplace.models';

type Step = 'branding' | 'agreement' | 'stripe' | 'done';

const SELLER_AGREEMENT_VERSION = '1.0';

@Component({
  selector: 'app-vendor-onboarding',
  templateUrl: './vendor-onboarding.component.html',
  styleUrls: ['./vendor-onboarding.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VendorOnboardingComponent implements OnInit {
  step: Step = 'branding';
  loading = true;
  /** Why the page couldn't load: an expired sign-in, or anything else. */
  loadError: 'signin' | 'other' | null = null;
  saving = false;
  vendor: Vendor | null = null;
  /** Stripe's view of the payout account (has the seller already sent their details?). */
  connectStatus: ConnectAccountStatus | null = null;

  shopName = '';
  bio = '';
  agreementChecked = false;

  readonly steps: { key: Step; label: string }[] = [
    { key: 'branding', label: 'Your shop' },
    { key: 'agreement', label: 'Agreement' },
    { key: 'stripe', label: 'Payouts' },
  ];

  constructor(
    private vendorService: VendorService,
    private toaster: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.vendorService.me().subscribe({
      next: (vendor) => {
        this.vendor = vendor;
        this.shopName = vendor.shop_name;
        this.bio = vendor.bio;
        this.step = this.resolveStep(vendor);
        this.loading = false;
        if (this.step === 'stripe') this.loadConnectStatus();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.status === 401 ? 'signin' : 'other';
        this.cdr.detectChanges();
      },
    });
  }

  private resolveStep(vendor: Vendor): Step {
    if (!vendor.shop_name) return 'branding';
    if (!vendor.terms_accepted_at) return 'agreement';
    if (!vendor.charges_enabled) return 'stripe';
    return 'done';
  }

  private loadConnectStatus(): void {
    this.vendorService.connectAccountStatus()
      .pipe(catchError(() => of(null)))
      .subscribe((status) => {
        this.connectStatus = status;
        this.cdr.detectChanges();
      });
  }

  /** Index of a step, for the tracker (done / current / upcoming). */
  stepIndex(step: Step): number {
    return step === 'done' ? this.steps.length : this.steps.findIndex((s) => s.key === step);
  }

  get currentIndex(): number {
    return this.stepIndex(this.step);
  }

  /** The agreement was already accepted (going back to it doesn't record it again). */
  get agreementAccepted(): boolean {
    return !!this.vendor?.terms_accepted_at;
  }

  /** Go back to an earlier step (Back buttons and the finished steps in the tracker). */
  goTo(step: Step): void {
    if (this.saving || this.stepIndex(step) >= this.currentIndex) return;
    this.step = step;
    if (step === 'agreement' && this.agreementAccepted) this.agreementChecked = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.cdr.detectChanges();
  }

  back(): void {
    const i = this.currentIndex;
    if (i > 0) this.goTo(this.steps[i - 1].key);
  }

  /** From an already accepted agreement straight on to payouts. */
  continueFromAgreement(): void {
    this.step = this.vendor?.charges_enabled ? 'done' : 'stripe';
    if (this.step === 'stripe') this.loadConnectStatus();
    this.cdr.detectChanges();
  }

  saveBranding(): void {
    if (!this.shopName.trim()) {
      this.toaster.showNote('Give your shop a name to continue.', 'warning');
      return;
    }
    this.saving = true;
    this.vendorService.updateMe({ shop_name: this.shopName.trim(), bio: this.bio.trim() }).subscribe({
      next: (vendor) => {
        this.vendor = vendor;
        this.saving = false;
        this.step = vendor.terms_accepted_at ? (vendor.charges_enabled ? 'done' : 'stripe') : 'agreement';
        if (this.step === 'stripe') this.loadConnectStatus();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.saving = false;
        this.toaster.showNote(err?.error?.shop_name?.[0] || err?.error?.bio?.[0] || 'Could not save your shop details.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  acceptAgreement(): void {
    if (!this.agreementChecked) {
      return;
    }
    this.saving = true;
    this.vendorService.acceptTerms(SELLER_AGREEMENT_VERSION).subscribe({
      next: (vendor) => {
        this.vendor = vendor;
        this.saving = false;
        this.step = vendor.charges_enabled ? 'done' : 'stripe';
        if (this.step === 'stripe') this.loadConnectStatus();
        this.cdr.detectChanges();
      },
      error: () => {
        this.saving = false;
        this.toaster.showNote('Could not record your agreement. Please try again.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  startStripeOnboarding(): void {
    this.saving = true;
    const returnUrl = `${window.location.origin}/marketplace/vendor-onboarding`;
    this.vendorService.connectOnboardingLink(returnUrl, returnUrl).subscribe({
      next: (res) => {
        window.location.href = res.url;
      },
      error: () => {
        this.saving = false;
        this.toaster.showNote('Could not start Stripe onboarding. Please try again.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  /** Free-only sellers don't need Stripe: go to the dashboard without it. */
  skipPayouts(): void {
    this.router.navigate(['/marketplace/vendor-dashboard']);
  }
}
